import fetcher from 'api/fetcher';
import { fetchCampaignInfo } from 'utils/campaign/fetchCampaignInfo';
import { AvailableCurrencies, CURRENCY_SYMBOLS } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { MagritteNotificationName } from 'utils/constants/notification';

import { formatCost } from '@hh.ru/front-static-app';

import { translate } from 'hooks/translation/useTrls';

import { CampaignFrontendStatus, setCampaigns, setCheckedCampaignIds } from 'store/models/campaignsDashboard';
import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { AppThunkAction } from 'store/utils';

export interface IncreaseBudgetResponse {
    updatedCampaignIds: Array<number>;
    failedCampaignIds: Array<number>;
}

export const batchIncreaseBudget =
    (increaseBudgetAmount: string, abortSignal: AbortSignal): AppThunkAction =>
    async (dispatch, getState) => {
        const campaignIds = getState().campaignsDashboard.campaignIdsForBudgetIncrease;
        const checkedCampaignIds = getState().campaignsDashboard.checkedCampaignIds;
        const campaigns = getState().campaignsDashboard.campaigns;
        const trls = getState().translations.trls;

        return fetcher
            .post<unknown, IncreaseBudgetResponse>(
                '/api/v1/campaigns/budget/batch',
                {
                    campaignIds: [...campaignIds],
                    addBudget: Number(increaseBudgetAmount),
                },
                {
                    signal: abortSignal,
                }
            )
            .then(async ({ updatedCampaignIds, failedCampaignIds }) => {
                const updatedIds = new Set(updatedCampaignIds);

                if (updatedIds.size === 0) {
                    dispatch(addMagritteNotification(MagritteNotificationName.CampaignBudgetIncreaseTotalFailed));
                    return;
                }

                const newCheckedCampaignIds = new Set(checkedCampaignIds);

                const refetchedCampaigns = await Promise.all(
                    [...updatedIds].map((id) => {
                        newCheckedCampaignIds.delete(id);
                        const storeCampaign = campaigns.get(id);

                        if (storeCampaign?.frontendStatus !== CampaignFrontendStatus.BudgetExceeded) {
                            return { id, refetchedCampaign: null };
                        }

                        return fetchCampaignInfo(id, abortSignal)
                            .then((campaign) => ({ id, refetchedCampaign: campaign }))
                            .catch(() => ({ id, refetchedCampaign: null }));
                    })
                );

                const newCampaignsList = new Map(campaigns);
                refetchedCampaigns.forEach(({ id, refetchedCampaign }) => {
                    const storeCampaign = newCampaignsList.get(id);
                    if (!storeCampaign) {
                        return;
                    }

                    if (!refetchedCampaign) {
                        // если кампанию не удалось перезапросить, меняем только бюджет
                        newCampaignsList.set(id, {
                            ...storeCampaign,
                            budget: Number(storeCampaign.budget || 0) + Number(increaseBudgetAmount),
                        });
                        return;
                    }

                    newCampaignsList.set(id, {
                        ...storeCampaign,
                        budget: Number(refetchedCampaign.budget),
                        frontendStatus: refetchedCampaign.frontendStatus,
                    });
                });

                const notification =
                    failedCampaignIds.length === 0
                        ? addMagritteCustomNotification({
                              type: MagritteNotificationType.Success,
                              text: translate(trls, 'BudgetIncreaseModal.notification.success', [
                                  `${formatCost(
                                      Number(increaseBudgetAmount),
                                      NON_BREAKING_SPACE
                                  )}${NON_BREAKING_SPACE}${CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}`,
                              ]),
                          })
                        : addMagritteNotification(MagritteNotificationName.CampaignBudgetIncreasePartialFailed);

                dispatch([setCheckedCampaignIds(newCheckedCampaignIds), setCampaigns(newCampaignsList), notification]);
            })
            .catch(() => {
                if (abortSignal.aborted) return;
                dispatch(addMagritteNotification(MagritteNotificationName.CampaignBudgetIncreaseTotalFailed));
            });
    };
